/**
 * ログイン方法（/account/login-methods、#112・#113、docs/43 §5、Figma「設定 ログイン方法」2枚）。#152（docs/61 ★3）から個人設定の「ログイン方法 ›」から開く
 *   （設定のメニューからは外した。前の /settings/login-methods は転送）。
 *   パスワード／Google／Apple の3行。右に 変更・パスワードを設定／追加／外す。
 *   下に「パスキー」の見出しと一覧（名前・追加日・最後に使った日、右に「外す」、名前は押して直せる）と「パスキー追加」。この製品のドメインの鍵だけ
 *   ・最後の1本（パスワードも他の外部も無い）は「外す」を押せない（API も 409 AUTH_LAST_LOGIN_METHOD）
 *   ・「追加」は属性変更の番人（海外は確認コード、docs/42）を通ってから Google・Apple へ移る。戻ると ?oauth_linked=google
 *   ・この製品で使えない外部（鍵が無い）は、つないでいなければ行を出さない
 *   人（アカウント）ごとの設定なので、担当者も自分の分を変えられる
 */
import {
  OAUTH_LABEL,
  OAUTH_MSG,
  OAUTH_PROVIDERS,
  OAuthProvider,
  PASSKEY_MSG,
  PASSWORD_MSG,
  PasskeyRenameRequest,
  PasswordSetRequest,
  oauthErrorText,
  type LoginMethods,
  type OAuthLinkStart,
  type Passkey,
} from "@genbaryoku/contract";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { OAuthLogo } from "../components/OAuthButtons.jsx";
import { PasskeyIcon, isPasskeyCancel, passkeyErrorText, passkeyRegister, syncPasskeySignals, usePasskeySupport } from "../components/Passkey.jsx";
import { useProduct } from "../lib/useProduct.js";
import { ErrorText, Field, Help, HiddenUsername, PasswordInput, PasswordRules, useFieldIds, useToast } from "../components/ui.jsx";

const KEY = ["login-methods"] as const;

/** 「2026/10/01」（日本時間） */
const ymd = (iso: string) => new Date(iso).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit" });
/** 「10/01」（日本時間） */
const md = (iso: string) => new Date(iso).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo", month: "2-digit", day: "2-digit" });

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" className="lmicon">
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function SettingsLoginMethodsPage() {
  usePageName(OAUTH_MSG.title);
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const { toast, node } = useToast();
  const [err, setErr] = useState<string | undefined>();
  const [setting, setSetting] = useState(false);
  const q = useQuery({ queryKey: KEY, queryFn: () => api<LoginMethods>("GET", "/account/login-methods") });
  const back = useBackNav({ to: "/account/profile", label: "個人設定" }, setting);

  // Google・Apple から戻った：?oauth_linked=google（追加できた）／?oauth_error=CODE（断られた）。読んだら URL から消す
  useEffect(() => {
    const linked = OAuthProvider.safeParse(sp.get("oauth_linked"));
    const e = sp.get("oauth_error");
    if (!linked.success && !e) return;
    if (linked.success) toast(OAUTH_MSG.linked(linked.data));
    if (e) setErr(oauthErrorText(e));
    setSp({}, { replace: true });
    // 最初の1回だけ（toast・setSp は毎回作られる）
  }, []);

  const add = useMutation({
    mutationFn: (p: OAuthProvider) => api<OAuthLinkStart>("POST", `/account/identities/${p}/start`),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => setErr(messageOf(e)),
  });
  const remove = useMutation({
    mutationFn: (p: OAuthProvider) => api<LoginMethods>("DELETE", `/account/identities/${p}`),
    onSuccess: (m, p) => {
      qc.setQueryData(KEY, m);
      setErr(undefined);
      toast(OAUTH_MSG.unlinked(p));
    },
    onError: (e) => setErr(messageOf(e)),
  });

  // パスキー（#113）
  const pk = usePasskeySupport();
  // ② 開いたとき：端末の候補をこの人の今の鍵の一覧と名前に合わせる（#114。関数の無いブラウザでは何もしない）
  useEffect(() => {
    void syncPasskeySignals();
  }, []);
  const P = useProduct();
  const [renaming, setRenaming] = useState<Passkey | null>(null);
  const addPk = useMutation({
    mutationFn: passkeyRegister,
    onSuccess: (m) => {
      qc.setQueryData(KEY, m);
      setErr(undefined);
      toast(PASSKEY_MSG.added);
    },
    onError: (e) => {
      if (!isPasskeyCancel(e)) setErr(passkeyErrorText(e));
    },
  });
  const removePk = useMutation({
    mutationFn: (id: string) => api<LoginMethods>("DELETE", `/account/passkeys/${id}`),
    onSuccess: (m) => {
      qc.setQueryData(KEY, m);
      setErr(undefined);
      toast(PASSKEY_MSG.removed);
      void syncPasskeySignals(); // ③ 外した直後：外した鍵を端末の候補から消す（#114）
    },
    onError: (e) => setErr(messageOf(e)),
  });

  const m = q.data;
  const busy = add.isPending || remove.isPending || addPk.isPending || removePk.isPending;
  const addPkButton = (
    <button type="button" className="btn tonal pkadd" disabled={busy || !pk.supported} onClick={() => addPk.mutate()} data-testid="lm-passkey-add">
      <PasskeyIcon />
      {PASSKEY_MSG.add}
    </button>
  );
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>
          {OAUTH_MSG.title}
          <Help testId="help-login-methods" text={OAUTH_MSG.help} />
        </h1>
        <span className="sp" />
      </div>
      <ErrorText>{err ?? (q.isError ? messageOf(q.error) : undefined)}</ErrorText>
      {!m ? (
        q.isPending ? <p className="hint">読み込んでいます…</p> : null
      ) : (
        <div className="paper lmcard" data-testid="login-methods">
          <div className="lmrow" data-testid="lm-password">
            <LockIcon />
            <div className="lmtext">
              <b>{OAUTH_MSG.password}</b>
              <span>{m.password.set ? OAUTH_MSG.passwordSet : OAUTH_MSG.passwordUnset}</span>
            </div>
            {m.password.set ? (
              <Link className="btn" to="/account/profile" data-testid="lm-password-change">
                {OAUTH_MSG.change}
              </Link>
            ) : (
              <button type="button" className="btn tonal" onClick={() => setSetting(true)} data-testid="lm-password-set">
                {OAUTH_MSG.setPassword}
              </button>
            )}
          </div>
          {OAUTH_PROVIDERS.filter((p) => m.available[p] || m[p]).map((p) => {
            const linked = m[p];
            const only = !!linked && m.count <= 1;
            return (
              <div className="lmrow" key={p} data-testid={`lm-${p}`}>
                <OAuthLogo provider={p} size={24} />
                <div className="lmtext">
                  <b>{OAUTH_LABEL[p]}</b>
                  <span>{linked ? (only ? OAUTH_MSG.onlyMethod(linked.email) : OAUTH_MSG.linkedOn(linked.email, ymd(linked.linkedAt))) : OAUTH_MSG.notLinked}</span>
                </div>
                {linked ? (
                  <button
                    type="button"
                    className="btn text"
                    disabled={only || busy}
                    aria-disabled={only}
                    title={only ? OAUTH_MSG.onlyMethod(linked.email) : undefined}
                    onClick={() => remove.mutate(p)}
                    data-testid={`lm-${p}-remove`}
                  >
                    {OAUTH_MSG.remove}
                  </button>
                ) : (
                  <button type="button" className="btn tonal" disabled={busy || !m.available[p]} onClick={() => add.mutate(p)} data-testid={`lm-${p}-add`}>
                    {OAUTH_MSG.add}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {m ? (
        <>
          <h2 className="lmhead">
            {PASSKEY_MSG.title}
            <Help testId="help-passkeys" text={PASSKEY_MSG.help(P.name)} />
          </h2>
          <div className="paper lmcard" data-testid="lm-passkeys">
            {m.passkeys.length === 0 ? (
              <div className="lmrow" data-testid="lm-passkeys-empty">
                <div className="lmtext">
                  <span>{PASSKEY_MSG.empty}</span>
                </div>
                {addPkButton}
              </div>
            ) : (
              <>
                {m.passkeys.map((p) => {
                  const only = m.count <= 1;
                  const sub = [PASSKEY_MSG.addedOn(ymd(p.createdAt)), p.lastUsedAt ? PASSKEY_MSG.lastUsed(md(p.lastUsedAt)) : null, only ? PASSKEY_MSG.onlyMethod : null].filter(Boolean).join(" ・ ");
                  return (
                    <div className="lmrow pkrow" key={p.id} data-testid="lm-passkey">
                      <PasskeyIcon size={24} className="lmicon pk" />
                      <div className="lmtext">
                        <button type="button" className="lmname" onClick={() => setRenaming(p)} aria-label={`${p.label}（${PASSKEY_MSG.rename}）`} data-testid="lm-passkey-name">
                          {p.label}
                        </button>
                        <span>{sub}</span>
                      </div>
                      <button
                        type="button"
                        className="btn text"
                        disabled={only || busy}
                        aria-disabled={only}
                        title={only ? PASSKEY_MSG.onlyMethod : undefined}
                        onClick={() => removePk.mutate(p.id)}
                        data-testid="lm-passkey-remove"
                      >
                        {OAUTH_MSG.remove}
                      </button>
                    </div>
                  );
                })}
                <div className="lmrow lmadd">{addPkButton}</div>
              </>
            )}
          </div>
          {pk.ready && !pk.supported ? <p className="hint">{PASSKEY_MSG.unsupported}</p> : null}
        </>
      ) : null}
      {renaming ? (
        <RenamePasskeyDialog
          passkey={renaming}
          onClose={() => setRenaming(null)}
          onDone={(next) => {
            qc.setQueryData(KEY, next);
            setRenaming(null);
            setErr(undefined);
            toast(PASSKEY_MSG.renamed);
          }}
        />
      ) : null}
      {setting && m ? (
        <SetPasswordDialog
          email={m.email}
          onClose={() => setSetting(false)}
          onDone={(next) => {
            qc.setQueryData(KEY, next);
            setSetting(false);
            setErr(undefined);
            toast(OAUTH_MSG.passwordSaved);
          }}
        />
      ) : null}
      {back.dialog}
      {node}
    </>
  );
}

/** パスワードの無い人（Google・Apple だけ）が初めて決める。メールアドレスをユーザー名の欄に置く（パスワード管理がユーザー名つきで覚える） */
function SetPasswordDialog({ email, onClose, onDone }: { email: string; onClose: () => void; onDone: (m: LoginMethods) => void }) {
  const ids = useFieldIds(["pw"] as const);
  const [err, setErr] = useState<string | undefined>();
  const form = useForm<PasswordSetRequest>({ resolver: zodResolver(PasswordSetRequest), defaultValues: { newPassword: "" }, shouldFocusError: true });
  const save = useMutation({
    mutationFn: async (v: PasswordSetRequest) => {
      await api("POST", "/account/password", v);
      return api<LoginMethods>("GET", "/account/login-methods");
    },
    onSuccess: onDone,
    onError: (e) => {
      if (e instanceof ApiError && e.problem.errors?.newPassword) {
        form.setError("newPassword", { message: String(e.problem.errors.newPassword) });
        return;
      }
      setErr(messageOf(e));
    },
  });
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && !save.isPending && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose, save.isPending]);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !save.isPending && onClose()}>
      <form
        className="dlg lmdlg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lm-set-h"
        noValidate
        data-testid="lm-set-dialog"
        onSubmit={form.handleSubmit((v) => {
          setErr(undefined);
          save.mutate(v);
        })}
      >
        {/* 「？」は題の右（欄の右に置くと欄だけ狭くなり、強さのバーと幅がずれる。S-00005） */}
        <div className="dh">
          <span id="lm-set-h">{OAUTH_MSG.setPasswordTitle}</span>
          <Help testId="help-set-password" text={`設定すると、${email} とパスワードでもログインできます。${PASSWORD_MSG.tip}`} />
        </div>
        <div className="db">
          <HiddenUsername email={email} />
          <Field id={ids.pw} label="新しいパスワード（8文字以上）">
            <PasswordInput id={ids.pw} autoComplete="new-password" reg={form.register("newPassword")} />
            <PasswordRules value={form.watch("newPassword") ?? ""} email={email} />
          </Field>
          <ErrorText>{form.formState.errors.newPassword?.message ?? err}</ErrorText>
        </div>
        <div className="df">
          <button type="submit" className="btn primary" disabled={save.isPending} data-testid="lm-set-submit">
            {OAUTH_MSG.setPasswordSubmit}
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={save.isPending}>
            {OAUTH_MSG.cancel}
          </button>
        </div>
      </form>
    </div>
  );
}

/** パスキーの名前を直す（「iCloud キーチェーン」→「事務所の Mac」など） */
function RenamePasskeyDialog({ passkey, onClose, onDone }: { passkey: Passkey; onClose: () => void; onDone: (m: LoginMethods) => void }) {
  const ids = useFieldIds(["label"] as const);
  const [err, setErr] = useState<string | undefined>();
  const form = useForm<PasskeyRenameRequest>({ resolver: zodResolver(PasskeyRenameRequest), defaultValues: { label: passkey.label }, shouldFocusError: true });
  const save = useMutation({
    mutationFn: (v: PasskeyRenameRequest) => api<LoginMethods>("PATCH", `/account/passkeys/${passkey.id}`, v),
    onSuccess: onDone,
    onError: (e) => setErr(messageOf(e)),
  });
  useEffect(() => {
    form.setFocus("label");
    const k = (e: KeyboardEvent) => e.key === "Escape" && !save.isPending && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose, save.isPending]);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !save.isPending && onClose()}>
      <form
        className="dlg lmdlg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lm-pk-h"
        noValidate
        data-testid="lm-passkey-rename"
        onSubmit={form.handleSubmit((v) => {
          setErr(undefined);
          save.mutate(v);
        })}
      >
        <div className="dh" id="lm-pk-h">
          {PASSKEY_MSG.rename}
        </div>
        <div className="db">
          <Field id={ids.label} label={PASSKEY_MSG.renameLabel}>
            <input id={ids.label} className="inp" maxLength={50} autoComplete="off" {...form.register("label")} />
          </Field>
          <ErrorText>{form.formState.errors.label?.message ?? err}</ErrorText>
        </div>
        <div className="df">
          <button type="submit" className="btn primary" disabled={save.isPending} data-testid="lm-passkey-rename-save">
            {PASSKEY_MSG.renameSave}
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={save.isPending}>
            {OAUTH_MSG.cancel}
          </button>
        </div>
      </form>
    </div>
  );
}
